'use es6';

import { useCallback, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import throttle from 'hs-lodash/throttle';
import { isInOnboardingSession } from 'onboarding-tours/util/bannerFrame';
import { getBannerContainer } from 'onboarding-tours/util/banner';
import { jsx as _jsx } from "react/jsx-runtime";
const makeStyleObject = (styleKey, styleValue) => ({
  styleKey,
  styleValue
});

// Copied from https://git.hubteam.com/HubSpot/theme-previewer/blob/master/theme-previewer/static/js/components/preview/PreloadedIframeProxy.js
// for now, but needs to be moved to shared repo before merging

// Note: this component runs when the proxy changes dimensions, but not when it moves.
// This is because ResizeObserver only fires when the dimensions of the observed element changes.

const useIsOnboardingBannerContainerOnDom = () => {
  const [isOnDom, setIsOnDom] = useState(isInOnboardingSession() && !!getBannerContainer());
  if (!isInOnboardingSession()) {
    return false;
  }
  if (!isOnDom) {
    const periodicallyCheckForBannerContainer = () => {
      const bannerContainer = getBannerContainer();
      if (bannerContainer) {
        setIsOnDom(true);
      } else {
        setTimeout(periodicallyCheckForBannerContainer, 100);
      }
    };
    periodicallyCheckForBannerContainer();
  }
  return isOnDom;
};
const PreloadedIframeProxyToExtract = ({
  proxyTop,
  proxyLeft,
  /**
   * proxyRight uses the proxied elements width to
   * calculate the right value
   */
  proxyRight,
  proxyHeight,
  proxyDisplay,
  iframeWrapperElement,
  iframeStylesManager
}) => {
  const proxyElRef = useRef(null);
  const isOnboardingContainerOnDom = useIsOnboardingBannerContainerOnDom();
  const resizeIframeWrapper = useCallback(() => {
    if (proxyElRef && proxyElRef.current) {
      // Make the iframe wrapper's bounds match the proxy
      const rect = proxyElRef.current.getBoundingClientRect();
      if (rect) {
        const styleObjects = [];
        const isHidden = rect.x === 0 && rect.y === 0 && rect.width === 0 && rect.height === 0;
        if (isHidden) {
          if (proxyDisplay) {
            iframeStylesManager.requestImmediateIframeStyleChange([makeStyleObject('display', 'none')]);
          }
        } else {
          if (proxyDisplay) {
            iframeStylesManager.requestImmediateIframeStyleChange([makeStyleObject('display', 'block')]);
          }
          if (proxyTop) {
            styleObjects.push(makeStyleObject('top', `${rect.top}px`));
          }
          if (proxyHeight) {
            styleObjects.push(makeStyleObject('height', `${rect.height}px`));
          }
          if (proxyLeft) {
            styleObjects.push(makeStyleObject('left', `${rect.left}px`));
          }
          if (proxyRight) {
            const right = iframeWrapperElement.parentElement.offsetWidth - rect.right;

            // right is modified instead of width to make animations smoother
            styleObjects.push(makeStyleObject('right', `${right}px`));
          }
        }
        if (styleObjects.length > 0) {
          iframeStylesManager.requestIframeStyleChangesFromProxy(styleObjects);
        }
      }
    }
  }, [proxyDisplay, proxyTop, proxyHeight, proxyLeft, proxyRight, iframeWrapperElement, iframeStylesManager]);

  // Bind a resize observer for this div, and proxy any resizes to the iframe element that lives outside
  // the tree. Also cleanup unmount
  useEffect(() => {
    resizeIframeWrapper();
    const throttledResizeFunc = throttle(resizeIframeWrapper, 100);
    const resizeObserver = new ResizeObserver(throttledResizeFunc);
    if (proxyElRef && proxyElRef.current) {
      resizeObserver.observe(proxyElRef.current, {
        box: 'content-box'
      });
      if (isOnboardingContainerOnDom) {
        const bannerContainer = getBannerContainer();
        if (bannerContainer) {
          resizeObserver.observe(bannerContainer, {
            box: 'content-box'
          });
        }
      }
    }
    return () => {
      if (proxyDisplay) {
        iframeStylesManager.requestImmediateIframeStyleChange([makeStyleObject('display', 'none')]);
      }
      resizeObserver.disconnect();
    };
  }, [proxyDisplay, iframeWrapperElement, resizeIframeWrapper, isOnboardingContainerOnDom, iframeStylesManager]);
  return /*#__PURE__*/_jsx("div", {
    className: "iframe-proxy",
    "data-test-id": "iframe-proxy",
    ref: proxyElRef,
    style: {
      width: '100%',
      height: '100%'
    }
  });
};
PreloadedIframeProxyToExtract.propTypes = {
  iframeWrapperElement: PropTypes.instanceOf(Element).isRequired,
  iframeStylesManager: PropTypes.object,
  proxyTop: PropTypes.bool.isRequired,
  proxyLeft: PropTypes.bool.isRequired,
  proxyRight: PropTypes.bool.isRequired,
  proxyHeight: PropTypes.bool.isRequired,
  proxyDisplay: PropTypes.bool.isRequired
};

/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
PreloadedIframeProxyToExtract.defaultProps = {
  proxyTop: false,
  proxyLeft: false,
  proxyRight: false,
  proxyHeight: false,
  proxyDisplay: false
};
export default PreloadedIframeProxyToExtract;